/**
 * Settings — a home for per-user preferences (everyone) plus an admin-only
 * "System" section (read-only server config + app SQLite backup actions).
 *
 * The per-user controls here are the SAME signals the contextual toolbars use
 * (lib/prefs.js + lib/theme.js), so this panel is a discoverable mirror, not a
 * second source of truth — flipping one updates the other live.
 */
import { createResource, createSignal, createEffect, For, Show } from 'solid-js';
import { currentUser } from '../lib/auth-store.js';
import { toast } from '../lib/toast.js';
import { theme, setTheme, THEMES } from '../lib/theme.js';
import {
  editorAutoFormat, setEditorAutoFormat, editorLineWrap, setEditorLineWrap,
  editorFontSize, setEditorFontSize,
  gridShowTypes, setGridShowTypes, gridPinPK, setGridPinPK,
  gridThousandSep, setGridThousandSep, gridShowHidden, setGridShowHidden,
  gridLimitEnabled, setGridLimitEnabled, gridRowLimit, setGridRowLimit,
  querySafeMode, setQuerySafeMode, queryTxByDefault, setQueryTxByDefault,
  querySoundNotify, setQuerySoundNotify,
  queryAutosaveResults, setQueryAutosaveResults,
  queryNotifyDefault, setQueryNotifyDefault,
  queryShowThreadId, setQueryShowThreadId,
  editInTransaction, setEditInTransaction, editRequireMode, setEditRequireMode,
  idleLockSeconds, setIdleLockSeconds,
  forceLogoutSeconds, setForceLogoutSeconds,
  backupSampleRows, setBackupSampleRows,
} from '../lib/prefs.js';
import { getServerInfo, listBackups, runBackupNow, backupDownloadUrl } from '../lib/settings.js';
import { getQueryNotifyConfig, setQueryNotifyConfig } from '../lib/querynotify.js';
import { getQuerySessionConfig, setQuerySessionConfig } from '../lib/querysession.js';
import { listTargets } from '../lib/notificationtargets.js';
import { getPinStatus, setPin, clearPin } from '../lib/pin.js';
import { lockNow } from '../lib/lock.js';
import { listAPIKeys, createAPIKey, revokeAPIKey, deleteAPIKey } from '../lib/apikeys.js';

function Toggle(props) {
  return (
    <label class="checkbox-label settings-toggle" title={props.hint || ''}>
      <input type="checkbox" checked={props.checked} onChange={(e) => props.onChange(e.target.checked)} />
      <span>{props.label}</span>
    </label>
  );
}

const fmtBytes = (n) => {
  if (!n && n !== 0) return '';
  const u = ['B', 'KB', 'MB', 'GB'];
  let i = 0, v = n;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(i ? 1 : 0)} ${u[i]}`;
};
const fmtDate = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

function APIKeysSection() {
  const [keys, { refetch }] = createResource(listAPIKeys);
  const [newName, setNewName] = createSignal('');
  const [createdToken, setCreatedToken] = createSignal(null);
  const [creating, setCreating] = createSignal(false);
  const [copied, setCopied] = createSignal(false);

  const doCreate = async () => {
    const name = newName().trim();
    if (!name) return;
    setCreating(true);
    try {
      const r = await createAPIKey(name);
      setCreatedToken(r.token);
      setNewName('');
      refetch();
    } catch (e) { toast.error('Failed to create key: ' + e.message); }
    finally { setCreating(false); }
  };

  const doRevoke = async (id) => {
    if (!confirm('Revoke this API key? Apps using it will no longer be able to authenticate.')) return;
    try { await revokeAPIKey(id); toast.success('Key revoked.'); refetch(); }
    catch (e) { toast.error('Revoke failed: ' + e.message); }
  };

  const doDelete = async (id) => {
    if (!confirm('Permanently delete this API key?')) return;
    try { await deleteAPIKey(id); toast.success('Key deleted.'); refetch(); }
    catch (e) { toast.error('Delete failed: ' + e.message); }
  };

  const copyToken = async () => {
    try {
      await navigator.clipboard.writeText(createdToken());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error('Copy failed — please select and copy manually.'); }
  };

  const fmtTime = (ts) => {
    if (!ts) return 'never';
    try { return new Date(ts * 1000).toLocaleString(); } catch { return '—'; }
  };

  return (
    <section class="settings-section">
      <h3>API Keys <span class="muted" style="font-weight:400;font-size:12px;">— for MCP clients (AI agents)</span></h3>
      <p class="muted" style="font-size:12px;margin:0 0 12px;">
        Create keys for AI agents to connect to Ninocat's MCP endpoint (<code>/api/mcp</code>) with your
        account's permissions. A key works <strong>only</strong> there — it can't log in to the web app or call
        any other API, so an agent can never approve its own AI Actions. The key is shown <strong>once</strong> on
        creation — copy it immediately.
      </p>

      <Show when={createdToken()}>
        <div class="api-key-created">
          <label style="font-size:12px;font-weight:600;">New API key — copy it now, it won't be shown again:</label>
          <div style="display:flex;gap:6px;align-items:center;margin-top:4px;">
            <input class="input mono" type="text" readOnly value={createdToken()}
                   style="flex:1;font-size:12px;" onClick={(e) => e.target.select()} />
            <button class="btn btn-sm" onClick={copyToken}>{copied() ? 'Copied!' : 'Copy'}</button>
            <button class="btn btn-sm btn-ghost" onClick={() => setCreatedToken(null)}>Dismiss</button>
          </div>
        </div>
      </Show>

      <div class="settings-row" style="margin-bottom:12px;">
        <input class="input" type="text" placeholder="Client name (e.g. Backup Script)"
               value={newName()} onInput={(e) => setNewName(e.target.value)}
               onKeyDown={(e) => { if (e.key === 'Enter') doCreate(); }}
               style="flex:1;max-width:300px;" />
        <button class="btn btn-primary" disabled={creating() || !newName().trim()} onClick={doCreate}>
          {creating() ? 'Creating…' : '+ Create key'}
        </button>
      </div>

      <Show when={!keys.loading} fallback={<p class="muted">Loading…</p>}>
        <Show when={(keys()?.keys || []).length > 0}
              fallback={<p class="muted" style="font-size:12px;">No API keys yet.</p>}>
          <table class="settings-backups mono" style="font-size:12px;">
            <thead><tr><th>Name</th><th>Created</th><th>Last used</th><th>Status</th><th></th></tr></thead>
            <tbody>
              <For each={keys()?.keys || []}>
                {(k) => (
                  <tr classList={{ 'api-key-revoked': k.revoked }}>
                    <td>{k.name}</td>
                    <td>{fmtTime(k.createdAt)}</td>
                    <td>{fmtTime(k.lastUsedAt)}</td>
                    <td>{k.revoked ? 'Revoked' : 'Active'}</td>
                    <td style="white-space:nowrap;">
                      <Show when={!k.revoked}>
                        <button class="btn btn-sm btn-ghost" onClick={() => doRevoke(k.id)}>Revoke</button>
                      </Show>
                      <button class="btn btn-sm btn-ghost btn-danger" onClick={() => doDelete(k.id)}>Delete</button>
                    </td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </Show>
      </Show>
    </section>
  );
}

export default function Settings() {
  const isAdmin = () => currentUser()?.role === 'admin';

  // Security section: PIN management for the idle-lock overlay.
  const [pinStatus, { refetch: refetchPin }] = createResource(getPinStatus);
  const [pinDraft, setPinDraft] = createSignal('');
  const [pinSaving, setPinSaving] = createSignal(false);
  const savePin = async () => {
    const v = pinDraft().replace(/[^0-9]/g, '');
    if (v.length < 4 || v.length > 6) { toast.error('PIN must be 4–6 digits.'); return; }
    setPinSaving(true);
    try {
      await setPin(v);
      toast.success('PIN saved.');
      setPinDraft('');
      refetchPin();
    } catch (e) { toast.error('Couldn’t save PIN: ' + e.message); }
    finally { setPinSaving(false); }
  };
  const removePin = async () => {
    if (!confirm('Remove your PIN? The idle lock will be disabled until you set a new one.')) return;
    setPinSaving(true);
    try { await clearPin(); toast.success('PIN removed.'); refetchPin(); }
    catch (e) { toast.error('Couldn’t remove PIN: ' + e.message); }
    finally { setPinSaving(false); }
  };

  // Query-completion notification: global config (enabled for all; target ids for
  // admins). Admins edit it here; everyone sees the per-user "by default" toggle
  // once it's enabled.
  const [qnCfg, { refetch: refetchQnCfg }] = createResource(getQueryNotifyConfig);
  const [qnTargets] = createResource(() => (isAdmin() ? 'go' : false), listTargets);
  const [qnEnabled, setQnEnabled] = createSignal(false);
  const [qnSuccess, setQnSuccess] = createSignal(0);
  const [qnFailure, setQnFailure] = createSignal(0);
  const [qnSaving, setQnSaving] = createSignal(false);
  createEffect(() => {
    const c = qnCfg();
    if (c) { setQnEnabled(!!c.enabled); setQnSuccess(c.successTargetId || 0); setQnFailure(c.failureTargetId || 0); }
  });
  const saveQn = async () => {
    setQnSaving(true);
    try {
      await setQueryNotifyConfig({ enabled: qnEnabled(), successTargetId: Number(qnSuccess()) || 0, failureTargetId: Number(qnFailure()) || 0 });
      toast.success('Query notification settings saved.');
      refetchQnCfg();
    } catch (e) { toast.error('Save failed: ' + e.message); }
    finally { setQnSaving(false); }
  };

  // Query-tab idle timeout (admin): minutes an idle tab keeps its pinned connection.
  const [qsCfg] = createResource(() => (isAdmin() ? 'go' : false), getQuerySessionConfig);
  const [qsIdle, setQsIdle] = createSignal('');
  const [qsSaving, setQsSaving] = createSignal(false);
  createEffect(() => { const c = qsCfg(); if (c) setQsIdle(String(c.idleMinutes)); });
  const saveQs = async () => {
    const n = Number(String(qsIdle()).trim());
    if (!Number.isInteger(n) || n < 1 || n > 1440) { toast.error('Enter a whole number of minutes between 1 and 1440.'); return; }
    setQsSaving(true);
    try {
      const r = await setQuerySessionConfig(n);
      setQsIdle(String(r.idleMinutes));
      toast.success(`Idle query tabs now release their connection after ${r.idleMinutes} min.`);
    } catch (e) { toast.error('Save failed: ' + e.message); }
    finally { setQsSaving(false); }
  };

  // Admin System section data (only fetched for admins).
  const [info] = createResource(() => (isAdmin() ? 'go' : false), getServerInfo);
  const [backups, { refetch: refetchBackups }] = createResource(() => (isAdmin() ? 'go' : false), listBackups);
  const [running, setRunning] = createSignal(false);
  const doRunBackup = async () => {
    setRunning(true);
    try {
      const r = await runBackupNow();
      toast.success(`Backup created: ${r.name}`);
      refetchBackups();
    } catch (e) {
      toast.error(`Backup failed: ${e.message}`);
    } finally {
      setRunning(false);
    }
  };

  return (
    <div class="page settings-page">
      <div class="settings-card">
      <div class="page-header"><h2>Settings</h2></div>

      <section class="settings-section">
        <h3>Theme</h3>
        <For each={['dark', 'light']}>
          {(base) => (
            <>
              <div class="theme-subhead">{base === 'dark' ? 'Dark' : 'Light'}</div>
              <div class="theme-grid">
                <For each={THEMES.filter((t) => t.base === base)}>
                  {(t) => (
                    <button class="theme-card" classList={{ active: theme() === t.id }}
                            onClick={() => setTheme(t.id)} title={t.label}>
                      <span class="theme-card-swatch" data-theme={t.id}>
                        <span class="tcs-accent" />
                        <span class="tcs-line tcs-line-1" />
                        <span class="tcs-line tcs-line-2" />
                      </span>
                      <span class="theme-card-name">{t.label}</span>
                    </button>
                  )}
                </For>
              </div>
            </>
          )}
        </For>
      </section>

      <section class="settings-section">
        <h3>Editor</h3>
        <Toggle label="Auto-format each statement on “;”" checked={editorAutoFormat()} onChange={setEditorAutoFormat}
                hint="Plain statements only; DELIMITER / routine scripts are skipped." />
        <Toggle label="Wrap long lines" checked={editorLineWrap()} onChange={setEditorLineWrap} />
        <div class="settings-row">
          <label>Font size</label>
          <input class="input settings-num" type="number" min="9" max="28" value={editorFontSize()}
                 onInput={(e) => setEditorFontSize(e.target.value)} />
          <span class="muted" style="font-size:12px;">px (or Ctrl+scroll in the editor)</span>
        </div>
      </section>

      <section class="settings-section">
        <h3>Grid</h3>
        <Toggle label="Show column types in headers" checked={gridShowTypes()} onChange={setGridShowTypes} />
        <Toggle label="Pin the primary-key column" checked={gridPinPK()} onChange={setGridPinPK} />
        <Toggle label="Thousand separators on numbers" checked={gridThousandSep()} onChange={setGridThousandSep} />
        <Toggle label="Show hidden databases" checked={gridShowHidden()} onChange={setGridShowHidden} />
        <Toggle label="Limit rows fetched" checked={gridLimitEnabled()} onChange={setGridLimitEnabled} />
        <div class="settings-row">
          <label>Row limit</label>
          <input class="input settings-num" type="number" min="1" max="100000" value={gridRowLimit()}
                 disabled={!gridLimitEnabled()} onInput={(e) => setGridRowLimit(e.target.value)} />
        </div>
      </section>

      <section class="settings-section">
        <h3>Query</h3>
        <div class="settings-row">
          <label class="settings-label">
            Safe Updates default
            <span class="muted" style="font-size:11px;display:block;font-weight:400;margin-top:2px;">
              Strict = MySQL's own guard (needs a key-based WHERE, composite PKs need the leftmost-prefix column).
              Lax tiers do the check client-side. Off lets bare DELETE FROM t run.
            </span>
          </label>
          <select class="input" value={querySafeMode()} onChange={(e) => setQuerySafeMode(e.currentTarget.value)}>
            <option value="strict">Strict (server-enforced)</option>
            <option value="lax-key">Lax — WHERE must hit a key column</option>
            <option value="lax-any">Lax — any WHERE / LIMIT</option>
            <option value="off">Off — no guard</option>
          </select>
        </div>
        <Toggle label="Wrap runs in a transaction by default" checked={queryTxByDefault()} onChange={setQueryTxByDefault} />
        <Toggle label="Play a sound when a run finishes" checked={querySoundNotify()} onChange={setQuerySoundNotify} />
        <Toggle label="Autosave results to Result History"
                hint="Save each query result as a CSV snapshot you can reopen read-only (large results are skipped). Off by default."
                checked={queryAutosaveResults()} onChange={setQueryAutosaveResults} />
        <Show when={qnCfg()?.enabled}>
          <Toggle label="Notify the configured target on query completion by default"
                  hint="New query tabs start with the Options-menu “Completion notification” toggle on. You can still flip it per tab."
                  checked={queryNotifyDefault()} onChange={setQueryNotifyDefault} />
        </Show>
        <Toggle label="Show the MySQL thread id in the toolbar" checked={queryShowThreadId()} onChange={setQueryShowThreadId} />
      </section>

      <section class="settings-section">
        <h3>Editing data <span class="muted" style="font-weight:400;font-size:12px;">— table-data + query-result grids</span></h3>
        <Toggle label="Wrap edits in a transaction"
                hint="On (default): edits stage in a transaction with an explicit Commit / Rollback. Off: each row change auto-commits immediately — faster, but there's no undo."
                checked={editInTransaction()} onChange={setEditInTransaction} />
        <Toggle label="Require entering edit mode before changing data (table-data grid)"
                hint="On (default): you must click “Edit” before a table's cells become editable. Off: cells are directly editable — take care, with the transaction toggle also off each change goes straight to the database. (Query-result grids always use the Edit button.)"
                checked={editRequireMode()} onChange={setEditRequireMode} />
      </section>

      <section class="settings-section">
        <h3>Backup</h3>
        <div class="settings-row">
          <label class="settings-label">
            Sample rows per table
            <span class="muted" style="font-size:11px;display:block;font-weight:400;margin-top:2px;">
              When a table is right-click-tagged “Backup: sample”, a database backup includes its
              structure plus this many rows (the first N). Structure-only and untagged tables are unaffected.
            </span>
          </label>
          <input class="input settings-num" type="number" min="1" max="1000000" value={backupSampleRows()}
                 onInput={(e) => setBackupSampleRows(e.target.value)} />
          <span class="muted" style="font-size:12px;">rows</span>
        </div>
      </section>

      <section class="settings-section">
        <h3>Security <span class="muted" style="font-weight:400;font-size:12px;">— idle lock + auto sign-out</span></h3>
        <p class="muted" style="font-size:12px;margin:0 0 12px;">
          The lock overlay is a casual deterrent — anyone with browser DevTools can dismiss it locally.
          The auto sign-out timer is the real safety net (it clears the server session).
        </p>

        <div class="settings-row">
          <label>Lock after</label>
          <input class="input settings-num" type="number" min="0" max="3600"
                 value={Math.round(idleLockSeconds() / 60)}
                 onInput={(e) => setIdleLockSeconds(Math.max(0, Math.min(3600, Number(e.target.value))) * 60)} />
          <span class="muted" style="font-size:12px;">minutes of inactivity (0 = off)</span>
        </div>

        <div class="settings-row">
          <label>Auto sign-out after</label>
          <input class="input settings-num" type="number" min="0" max="86400"
                 value={Math.round(forceLogoutSeconds() / 60)}
                 onInput={(e) => setForceLogoutSeconds(Math.max(0, Math.min(1440, Number(e.target.value))) * 60)} />
          <span class="muted" style="font-size:12px;">minutes of inactivity (0 = off)</span>
        </div>

        <div class="settings-row">
          <label>PIN</label>
          <Show when={!pinStatus.loading} fallback={<span class="muted">Loading…</span>}>
            <Show when={!pinStatus()?.enabled}>
              <input class="input settings-num" type="password" inputmode="numeric" maxlength="6"
                     placeholder="4–6 digits"
                     value={pinDraft()} onInput={(e) => setPinDraft(e.target.value)} />
              <button class="btn" disabled={pinSaving() || !pinDraft()} onClick={savePin}>Set PIN</button>
            </Show>
            <Show when={pinStatus()?.enabled}>
              <span class="muted" style="font-size:12px;">PIN configured.</span>
              <input class="input settings-num" type="password" inputmode="numeric" maxlength="6"
                     placeholder="New 4–6 digits"
                     value={pinDraft()} onInput={(e) => setPinDraft(e.target.value)} />
              <button class="btn btn-sm" disabled={pinSaving() || !pinDraft()} onClick={savePin}>Change</button>
              <button class="btn btn-sm btn-danger" disabled={pinSaving()} onClick={removePin}>Remove</button>
            </Show>
          </Show>
        </div>

        <Show when={pinStatus()?.enabled && idleLockSeconds() > 0}>
          <div class="settings-row">
            <label>Lock now</label>
            <button class="btn btn-sm" onClick={lockNow}>🔒 Lock workspace</button>
          </div>
        </Show>
      </section>

      <APIKeysSection />

      <Show when={isAdmin()}>
        <section class="settings-section">
          <h3>Query tabs <span class="muted" style="font-weight:400;font-size:12px;">— idle connections (all users)</span></h3>
          <p class="muted" style="font-size:12px;margin:0 0 12px;">
            Each open query tab holds its own database connection. After this many minutes without use, the connection is
            released; the tab silently opens a fresh one the next time it runs. A tab with a running query or an open
            transaction is never released. Query tabs use their own connections, so they never slow down grids, tools or AI agents.
          </p>
          <div class="settings-row">
            <label>Release an idle tab's connection after</label>
            <input class="input" type="number" min="1" max="1440" step="1" style="width:90px;"
                   value={qsIdle()} onInput={(e) => setQsIdle(e.currentTarget.value)}
                   onKeyDown={(e) => { if (e.key === 'Enter') saveQs(); }} />
            <span class="muted">minutes</span>
          </div>
          <div class="settings-row">
            <button class="btn btn-primary" disabled={qsSaving()} onClick={saveQs}>{qsSaving() ? 'Saving…' : 'Save'}</button>
          </div>
        </section>

        <section class="settings-section">
          <h3>Query completion notification <span class="muted" style="font-weight:400;font-size:12px;">— notify a target when a query finishes</span></h3>
          <p class="muted" style="font-size:12px;margin:0 0 12px;">
            When enabled, query tabs gain a “Completion notification” toggle in their ⚙ Options menu. With it on, finishing a
            run notifies the chosen target — on success or failure, like a scheduled job. Manage targets in
            {' '}<strong>Tools → Notification Targets</strong>. Non-admins never see the targets; they only opt in per tab.
          </p>
          <Toggle label="Enable query completion notifications" checked={qnEnabled()} onChange={setQnEnabled} />
          <Show when={qnEnabled()}>
            <div class="settings-row">
              <label>On success → target</label>
              <select class="input" value={qnSuccess()} onChange={(e) => setQnSuccess(Number(e.currentTarget.value))}>
                <option value="0">— none —</option>
                <For each={qnTargets() || []}>{(t) => <option value={t.id}>{t.name}</option>}</For>
              </select>
            </div>
            <div class="settings-row">
              <label>On failure → target</label>
              <select class="input" value={qnFailure()} onChange={(e) => setQnFailure(Number(e.currentTarget.value))}>
                <option value="0">— none —</option>
                <For each={qnTargets() || []}>{(t) => <option value={t.id}>{t.name}</option>}</For>
              </select>
            </div>
            <Show when={(qnTargets() || []).length === 0}>
              <p class="muted" style="font-size:12px;">No notification targets yet — create one in Tools → Notification Targets first.</p>
            </Show>
          </Show>
          <div class="settings-row">
            <button class="btn btn-primary" disabled={qnSaving()} onClick={saveQn}>{qnSaving() ? 'Saving…' : 'Save'}</button>
          </div>
        </section>

        <section class="settings-section">
          <h3>System <span class="muted" style="font-weight:400;font-size:12px;">— server configuration (read-only)</span></h3>
          <Show when={info()} fallback={<p class="muted">Loading…</p>}>
            <table class="settings-info mono">
              <tbody>
                <tr><td>Bind address</td><td>{info().addr}</td></tr>
                <tr><td>Database file</td><td>{info().dbPath}</td></tr>
                <tr><td>Session lifetime</td><td>{info().sessionTtlHours} h</td></tr>
                <tr><td>Auto-backup</td><td>{info().backupEnabled ? `every ${info().backupInterval}, keep ${info().backupKeep}` : 'disabled'}</td></tr>
                <tr><td>Backup directory</td><td>{info().backupDir}</td></tr>
              </tbody>
            </table>
          </Show>
          <p class="muted" style="font-size:12px;margin:6px 0 10px;">
            These are set via environment variables at startup. Changing them means editing the server's
            {' '}<code class="mono">.env</code> and restarting.
          </p>

          <div class="settings-row">
            <label>App backups</label>
            <button class="btn" disabled={running()} onClick={doRunBackup}>
              {running() ? 'Backing up…' : '⤓ Run backup now'}
            </button>
          </div>
          <Show when={(backups()?.backups || []).length}
                fallback={<p class="muted" style="font-size:12px;">No backups yet.</p>}>
            <table class="settings-backups mono">
              <thead><tr><th>File</th><th>Size</th><th>Created</th><th></th></tr></thead>
              <tbody>
                <For each={backups().backups}>
                  {(b) => (
                    <tr>
                      <td>{b.name}</td>
                      <td>{fmtBytes(b.size)}</td>
                      <td>{fmtDate(b.modTime)}</td>
                      <td><a class="btn btn-sm btn-ghost" href={backupDownloadUrl(b.name)} download>Download</a></td>
                    </tr>
                  )}
                </For>
              </tbody>
            </table>
          </Show>
        </section>
      </Show>
      </div>
    </div>
  );
}
